import { Button, 
         DialogContent, 
         TextField
       }                          from "@mui/material"
import React, 
       { useState, 
         useCallback, 
         useEffect 
       }                          from "react"
import { BglDialog, 
         BglDialogTitle, 
         BglDialogActions 
       }                          from "components/dialog/BglDialogStyles"
import { APIData }                from "types/cms/frontendTypes"
import { CMS_DEFAULT_LOCALE }     from "store/dataReducers/cmsPilotSlice"
import { getLocalizedTemplateQuestionTitle } from "../utils/SurveyTemplateLocalizationUtils"
import { useDispatch } from "react-redux"
import { setUnsavedTemplateAction } from "store/dataReducers/surveyTemplateSlice"

interface SurveyTemplateEditQuestionTitleDialogProps {
  open : boolean
  onClose : () => void
  template : APIData<'api::survey-template.survey-template'>
  questionUid : string
  questionTitle : string
  selectedLocale : string
}

export const SurveyTemplateEditQuestionTitleDialog: React.FC<SurveyTemplateEditQuestionTitleDialogProps> = ({
  open,
  onClose,
  template,
  questionUid,
  questionTitle,
  selectedLocale,
}) => {
  const [editTitleValue, setEditTitleValue] = useState('')
  const dispatch = useDispatch()

  // Initialize edit title value when dialog opens
  useEffect(() => {
    if (open) {
      setEditTitleValue(questionTitle || '')
    }
  }, [open, questionTitle])

  const handleClose = useCallback(() => {
    onClose()
    setEditTitleValue('')
  }, [onClose])

  const handleSaveTitle = useCallback(() => {
    // Find the templateQuestion by questionUid
    const templateQuestionIndex = template.templateQuestions.findIndex(
      tq => tq.questionUid === questionUid
    )
    
    const templateQuestion = templateQuestionIndex >= 0 
      ? template.templateQuestions[templateQuestionIndex]
      : null
    
    const newTitle = editTitleValue.trim()
    
    // Get current title for the selected locale
    // If templateQuestion doesn't exist, create a minimal one to check localizations
    const templateQuestionForCheck = templateQuestion || {
      questionUid,
      isPrimary: false,
      displayOrder: 0,
      title: undefined,
      branchingRules: [],
    } as any
    
    const currentTitle = getLocalizedTemplateQuestionTitle(template, templateQuestionForCheck, selectedLocale) || ''
    
    // Validate that the title has actually changed
    if (newTitle === currentTitle) {
      handleClose()
      return
    }
    
    let updatedTemplate: Partial<APIData<'api::survey-template.survey-template'>>
    
    if (selectedLocale === CMS_DEFAULT_LOCALE) {
      // For default locale, update templateQuestion.title directly
      if (templateQuestionIndex >= 0) {
        // Update existing templateQuestion
        const updatedTemplateQuestions = template.templateQuestions.map(tq => {
          if (tq.questionUid === questionUid) {
            return {
              ...tq,
              title: newTitle || undefined, // Set to undefined if empty string to use default from question bank
            }
          }
          return tq
        })
        
        updatedTemplate = {
          templateQuestions: updatedTemplateQuestions,
        }
      } else {
        // Child question doesn't exist in templateQuestions yet - need to add it
        // Only create if newTitle is not empty (if empty, no override needed, so don't create entry)
        if (newTitle) {
          const newTemplateQuestion = {
            id: 0, // Will be assigned by backend
            isPrimary: false,
            displayOrder: 0,
            title: newTitle,
            questionUid: questionUid,
            branchingRules: [], // Child questions can have their own branching rules
          }
          
          updatedTemplate = {
            templateQuestions: [...template.templateQuestions, newTemplateQuestion],
          }
        } else {
          // No change needed if trying to create empty override for non-existent child question
          handleClose()
          return
        }
      }
    } else {
      // For non-default locale, update localizations array
      const localizations: any[] = (template as any).localizations || []
      const localeDataIndex = localizations.findIndex((l: any) => l.locale === selectedLocale)
      
      let updatedLocalizations: any[]
      
      if (localeDataIndex >= 0) {
        // Update existing localization
        const localeData = localizations[localeDataIndex]
        const templateQuestionIndexInLocale = localeData.templateQuestions?.findIndex(
          (tq: any) => tq.questionUid === questionUid
        ) ?? -1
        
        if (templateQuestionIndexInLocale >= 0) {
          // Update existing template question in localization
          const updatedTemplateQuestions = [...(localeData.templateQuestions || [])]
          updatedTemplateQuestions[templateQuestionIndexInLocale] = {
            ...updatedTemplateQuestions[templateQuestionIndexInLocale],
            title: newTitle || undefined,
          }
          updatedLocalizations = [...localizations]
          updatedLocalizations[localeDataIndex] = {
            ...localeData,
            templateQuestions: updatedTemplateQuestions,
          }
        } else {
          // Add new template question to existing localization
          const updatedTemplateQuestions = [
            ...(localeData.templateQuestions || []),
            {
              questionUid,
              title: newTitle || undefined,
            },
          ]
          updatedLocalizations = [...localizations]
          updatedLocalizations[localeDataIndex] = {
            ...localeData,
            templateQuestions: updatedTemplateQuestions,
          }
        }
      } else {
        // Create new localization entry
        updatedLocalizations = [
          ...localizations,
          {
            locale: selectedLocale,
            templateQuestions: [
              {
                questionUid,
                title: newTitle || undefined,
              },
            ],
          },
        ]
      }
      
      updatedTemplate = {
        localizations: updatedLocalizations as any,
      }
    }
    
    dispatch(setUnsavedTemplateAction({
      documentId: template.documentId!,
      diff: updatedTemplate,
    }))

    handleClose()
  }, [template, questionUid, editTitleValue, selectedLocale, handleClose, dispatch])

  return (
    <BglDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <BglDialogTitle
        title="Edit Question Title"
        onClose={handleClose}
      />
      <DialogContent>
        <TextField
          label="Question Title"
          fullWidth
          value={editTitleValue}
          onChange={(e) => setEditTitleValue(e.target.value)}
          autoFocus
          sx={{ mt: 1 }}
          placeholder="Enter question title (leave empty to use default)"
          helperText="Leave empty to use the default question title from the question bank"
        />
      </DialogContent>
      <BglDialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveTitle}
          variant="contained"
        >
          Update Title
        </Button>
      </BglDialogActions>
    </BglDialog>
  )
}
